<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { useElementVisibility, useResizeObserver } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { useUiStore } from '@/stores/ui'
import { cssPxFromVisual, PAGE_ZOOM_EVENT } from '@/lib/pageZoom'

/** Длительность width-transition сайдбара (+ небольшой запас под reflow). */
const SIDEBAR_WIDTH_TRANSITION_MS = 320

const props = defineProps({
  /** Ref на элемент в основном контенте: при его видимости стики-панель скрывается */
  alsoHideWhenVisibleRef: {
    type: Object,
    default: null,
  },
  /** Только fixed-панель (без ряда кнопок в потоке документа). */
  stickyOnly: {
    type: Boolean,
    default: false,
  },
})
const { fullscreen, sidebarCollapsed } = storeToRefs(useUiStore())
const staticPanel = ref(null)
const mounted = ref(false)
const staticPanelLeft = ref(0)
let sidebarLeftRaf = 0
const staticPanelVisible = useElementVisibility(staticPanel, {
  threshold: 0.01,
})

const contentRef = computed(() => props.alsoHideWhenVisibleRef?.value ?? null)
const contentVisible = useElementVisibility(contentRef, {
  threshold: 0.01,
})

const showSticky = computed(() => {
  if (!mounted.value) {
    return false
  }
  if (props.stickyOnly) {
    return true
  }
  return !staticPanelVisible.value && !contentVisible.value
})
const stickyStyle = computed(() => ({
  position: 'fixed',
  left: '0',
  right: '0',
  bottom: '0',
  '--sticky-panel-content-left': `${staticPanelLeft.value}px`,
}))

/**
 * Синхронизирует left кнопок fixed-панели с положением static-якоря в потоке.
 */
function updateStaticPanelLeft() {
  // getBoundingClientRect — уже после CSS zoom на <html>.
  // padding-left задаётся в CSS-px до zoom, иначе кнопки уезжают вправо.
  const raw = staticPanel.value?.getBoundingClientRect().left || 0
  staticPanelLeft.value = Math.max(0, Math.round(cssPxFromVisual(raw)))
}

/**
 * Пересчитывает left на каждом кадре, пока сайдбар анимирует ширину.
 */
function trackStaticPanelLeftDuringSidebarTransition() {
  if (sidebarLeftRaf) {
    cancelAnimationFrame(sidebarLeftRaf)
    sidebarLeftRaf = 0
  }
  const startedAt = performance.now()
  const tick = now => {
    updateStaticPanelLeft()
    if (now - startedAt < SIDEBAR_WIDTH_TRANSITION_MS) {
      sidebarLeftRaf = requestAnimationFrame(tick)
      return
    }
    sidebarLeftRaf = 0
  }
  sidebarLeftRaf = requestAnimationFrame(tick)
}

// Ширина якоря меняется вместе с main при collapse — ловим и mid-transition.
useResizeObserver(staticPanel, updateStaticPanelLeft)

onMounted(() => {
  mounted.value = true
  nextTick(updateStaticPanelLeft)
  window.addEventListener('resize', updateStaticPanelLeft)
  window.addEventListener(PAGE_ZOOM_EVENT, updateStaticPanelLeft)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateStaticPanelLeft)
  window.removeEventListener(PAGE_ZOOM_EVENT, updateStaticPanelLeft)
  if (sidebarLeftRaf) {
    cancelAnimationFrame(sidebarLeftRaf)
    sidebarLeftRaf = 0
  }
})

watch(showSticky, async () => {
  await nextTick()
  updateStaticPanelLeft()
})

// Иначе left остаётся от прежней ширины меню, пока sticky не скроется и не появится снова.
watch(sidebarCollapsed, async () => {
  await nextTick()
  updateStaticPanelLeft()
  trackStaticPanelLeftDuringSidebarTransition()
})
</script>
<template>
  <div
    ref="staticPanel"
    :class="
      stickyOnly
        ? 'sticky-panel-anchor'
        : 'hstack gap-3 align-items-center flex-wrap'
    "
  >
    <template v-if="!stickyOnly">
      <slot name="static">
        <slot />
      </slot>
    </template>
  </div>

  <Teleport to="body">
    <div
      v-if="showSticky"
      class="sticky-panel stick pe-4"
      :class="{ 'sticky-panel--fullscreen': fullscreen }"
      :style="stickyStyle"
    >
      <div class="sticky-panel__inner container-fluid hstack gap-3 align-items-center flex-wrap">
        <slot name="sticky">
          <slot />
        </slot>
      </div>
    </div>
  </Teleport>
</template>

<style>
.sticky-panel-anchor {
  height: 0;
  margin: 0;
  padding: 0;
  overflow: hidden;
  pointer-events: none;
}
</style>
